// @ts-nocheck
import PropTypes from "prop-types";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {z} from "zod";
import {BaseButton, BaseDatePicker, BaseInput, BaseSelect, BaseTooltip, toast} from "@/components";
import useIsMobile from "@/hooks/useIsMobile";
import {adapterBankList} from "../../../../adapters/bankListAdapter";
import copyBtn from "../../../../assets/images/modal-icons/copy-btn.svg";
import useDepositPagoMovil from "../../../../hooks/wallet/usePostDepositPagoMovil";
import {useAppConfig} from "../../../../store/useAppConfig.store";
import * as styles from "../_bankOptionsCls";

const buildSchema = t =>
  z.object({
    referencia: z.string().trim().min(1, t("depositInstructionsBankVzlaReference")),
    importe: z.string().trim().min(1, t("depositInstructionsBankVzlaAmount")),
    fecha: z.string().min(1, t("depositInstructionsBankVzlaDate")),
    banco_origen: z.string().min(1, t("mobilePaymentModalSelectBankMessage")),
  });

const BankVzla = ({onBack}) => {
  const RIF = useAppConfig(state => state.RIF);
  const PagoMovilNumber = useAppConfig(state => state.PagoMovilNumber);
  const {t} = useTranslation();
  const isMobile = useIsMobile();

  const [showForm, setShowForm] = useState(false);
  const [values, setValues] = useState({referencia: "", importe: "", fecha: "", banco_origen: ""});
  const [errors, setErrors] = useState({});
  const {mutate, isPending} = useDepositPagoMovil();

  const setField = (field, value) => {
    setValues(v => ({...v, [field]: value}));
    if (errors[field]) setErrors(prev => ({...prev, [field]: undefined}));
  };
  const handleInput = field => e => setField(field, e.target.value);

  const canSubmit = !!(values.referencia && values.importe && values.fecha && values.banco_origen);

  const handleDeposit = e => {
    e.preventDefault();
    const result = buildSchema(t).safeParse(values);
    if (!result.success) {
      const nextErrors = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0];
        if (typeof key === "string" && !nextErrors[key]) nextErrors[key] = issue.message;
      }
      setErrors(nextErrors);
      return;
    }
    mutate({
      referencia: result.data.referencia,
      importe: Number.parseFloat(result.data.importe),
      fecha: result.data.fecha,
      banco_origen: `0${result.data.banco_origen}`,
    });
  };

  const copyToClipboard = async text => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("depositInstructionsCopied"));
    } catch {
      toast.error(t("depositInstructionsError"));
    }
  };

  const handleShowForm = () => {
    setShowForm(true);
  };

  const handleBackToInfo = () => {
    setValues({referencia: "", importe: "", fecha: "", banco_origen: ""});
    setErrors({});
    setShowForm(false);
  };

  const bankOptions = adapterBankList();

  const renderFormFields = ({mobile}) => (
    <>
      <div style={{marginBottom: "var(--space-4-5)"}}>
        {mobile ? (
          <div className={styles.mobileInputWrapper}>
            <BaseInput
              placeholder={t("depositInstructionsBankVzlaReference")}
              containerClassName={styles.mobileFormInput}
              value={values.referencia}
              onChange={handleInput("referencia")}
              error={errors.referencia}
            />
            <span className={styles.redAsterisk}>*</span>
          </div>
        ) : (
          <BaseInput
            label={t("depositInstructionsBankVzlaReference")}
            placeholder="000191969387"
            value={values.referencia}
            onChange={handleInput("referencia")}
            error={errors.referencia}
          />
        )}
      </div>

      <div style={{marginBottom: "var(--space-4-5)"}}>
        {mobile ? (
          <div className={styles.mobileInputWrapper}>
            <BaseInput
              placeholder={t("depositInstructionsBankVzlaAmount")}
              containerClassName={styles.mobileFormInput}
              value={values.importe}
              onChange={handleInput("importe")}
              error={errors.importe}
            />
            <span className={styles.redAsterisk}>*</span>
          </div>
        ) : (
          <BaseInput
            label={t("depositInstructionsBankVzlaAmount")}
            value={values.importe}
            onChange={handleInput("importe")}
            error={errors.importe}
          />
        )}
      </div>

      <div style={{marginBottom: "var(--space-4-5)"}}>
        <BaseDatePicker
          label={mobile ? undefined : t("depositInstructionsBankVzlaDate")}
          type="date"
          value={values.fecha}
          onChange={value => setField("fecha", value ?? "")}
          placeholder={mobile ? t("depositInstructionsBankVzlaDate") : t("depositInstructionsBankVzlaSelectDate")}
          error={errors.fecha}
        />
      </div>

      <div style={{marginBottom: "var(--space-4-5)"}}>
        {!mobile && <label className={styles.bankLabel}>{t("mobilePaymentModalBank")}:</label>}
        {mobile && <h4 style={{margin: 0}}>{t("depositInstructionsBankVzlaBankOptions")}</h4>}
        <BaseSelect
          value={values.banco_origen}
          onChange={e => setField("banco_origen", e.target.value)}
          placeholder={t("mobilePaymentModalSelectBank")}
          options={bankOptions}
          error={errors.banco_origen}
          showSearch
        />
      </div>
    </>
  );

  return (
    <div className={styles.bankOptionsModalContainer}>
      {isMobile ? (
        !showForm ? (
          <div className={styles.mobileModal}>
            <h4 className={styles.mobileModalTitle}>{t("depositInstructions")}</h4>
            <p className={styles.mobileInstructionText}>{t("depositInstructionsMessageVzla")}</p>
            <h4
              style={{
                fontFamily: "var(--font-family)",
                fontWeight: 700,
                textAlignLast: "center",
                fontStyle: "normal",
                lineHeight: "var(--leading-md)",
                letterSpacing: "0%",
                textAlign: "justify",
                color: "var(--color-text-secondary)",
                marginTop: "1rem",
                marginBottom: "1rem",
              }}
            >
              {t("depositInstructionsCopyBtnTitle")}
            </h4>

            <div className={styles.mobileButtons}>
              <div className={styles.bankRow}>
                <p className={styles.bankLabel}>RIF</p>
                <button
                  type="button"
                  className={styles.mobileCopyButton}
                  onClick={() => copyToClipboard(RIF)}
                >
                  <img
                    src={copyBtn}
                    alt="copy"
                  />
                  <span>{RIF}</span>
                </button>
              </div>
              <div className={styles.bankRow}>
                <p className={styles.bankLabel}>{t("depositInstructionsMobilePhone")}</p>
                <button
                  type="button"
                  className={styles.mobileCopyButton}
                  onClick={() => copyToClipboard(PagoMovilNumber)}
                >
                  <img
                    src={copyBtn}
                    alt="copy"
                  />
                  <span>{PagoMovilNumber}</span>
                </button>
              </div>
            </div>

            <div className={styles.mobileFormularioSection}>
              <BaseButton
                variant="primary"
                className="btn-tertiary-hover"
                onClick={handleShowForm}
                style={{
                  width: 261,
                  minHeight: 72,
                  borderRadius: "var(--radius-lg)",
                  padding: "25px 10px",
                  fontSize: "var(--font-h3)",
                  fontWeight: 600,
                }}
              >
                {t("depositInstructionsBankVzlaForm")}
              </BaseButton>
              <BaseButton
                variant="secondary"
                className="btn-secondary-hover"
                onClick={onBack}
                style={{
                  width: 261,
                  minHeight: 72,
                  borderRadius: "var(--radius-lg)",
                  padding: "25px 10px",
                  fontSize: "var(--font-h3)",
                  fontWeight: 600,
                }}
              >
                {t("depositInstructionsBack")}
              </BaseButton>
            </div>
          </div>
        ) : (
          <div className={`${styles.mobileModal} mobile-bank-form`}>
            <h4 className={styles.mobileModalTitle}>{t("depositInstructionsBankVzlaFormTitle")}</h4>
            <p>{t("depositInstructionsBankVzlaFormInfo")}</p>
            <form
              onSubmit={handleDeposit}
              noValidate
            >
              <div className={styles.mobileFormFields}>{renderFormFields({mobile: true})}</div>
              <div className={styles.mobileFormActions}>
                <BaseButton
                  variant="primary"
                  className="btn-tertiary-hover"
                  type="submit"
                  loading={isPending}
                  disabled={isPending || !canSubmit}
                >
                  {t("depositInstructionsContinue")}
                </BaseButton>
                <BaseButton
                  variant="secondary"
                  className="btn-secondary-hover"
                  onClick={handleBackToInfo}
                >
                  {t("depositInstructionsBack")}
                </BaseButton>
                <BaseButton
                  variant="secondary"
                  className="btn-secondary-hover"
                  onClick={onBack}
                >
                  {t("depositInstructionsCancel")}
                </BaseButton>
              </div>
            </form>
          </div>
        )
      ) : (
        <>
          <h4 className={styles.bankOptions_instruction_title}>{t("depositInstructions")}</h4>
          <div style={{textAlign: "center"}}>
            <p style={{fontSize: "var(--primary-font-size)", textAlign: "left"}}>
              {t("depositInstructionsMessageVzla")}
            </p>
          </div>

          <div
            style={{
              marginBottom: 42,
              marginTop: "var(--space-7)",
              marginLeft: 70,
              display: "flex",
              justifyContent: "center",
              gap: "var(--space-4-5)",
              flexWrap: "wrap",
            }}
          >
            <div style={{flex: "0 1 41.666%", maxWidth: "41.666%"}}>
              <div
                className={styles.bankRowCentered}
                style={{
                  fontSize: "var(--primary-font-size)",
                  justifyContent: "center",
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--space-3-5)",
                }}
              >
                <BaseTooltip title={t("depositInstructionsCopy")}>
                  <button
                    type="button"
                    className={styles.copyBtnImg}
                    onClick={() => copyToClipboard(RIF)}
                    style={{background: "transparent", border: "none", padding: 0, cursor: "pointer"}}
                    aria-label="copy"
                  >
                    <img
                      src={copyBtn}
                      alt="copy"
                      className={styles.copyBtnImg}
                    />
                  </button>
                </BaseTooltip>
                <div className={styles.bankLabelValue}>
                  <span className={styles.bankLabel}>RIF</span>
                  <span className={styles.bankValue}>{RIF}</span>
                </div>
              </div>
            </div>
            <div style={{flex: "0 1 41.666%", maxWidth: "41.666%"}}>
              <div
                className={styles.bankRowCentered}
                style={{
                  justifyContent: "center",
                  fontSize: "var(--primary-font-size)",
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--space-3-5)",
                }}
              >
                <BaseTooltip title={t("depositInstructionsCopy")}>
                  <button
                    type="button"
                    className={styles.copyBtnImg}
                    onClick={() => copyToClipboard(PagoMovilNumber)}
                    style={{background: "transparent", border: "none", padding: 0, cursor: "pointer"}}
                    aria-label="copy"
                  >
                    <img
                      src={copyBtn}
                      alt="copy"
                      className={styles.copyBtnImg}
                    />
                  </button>
                </BaseTooltip>
                <div
                  className={styles.bankLabelValue}
                  style={{alignItems: "flex-start"}}
                >
                  <span className={styles.bankLabel}>{t("depositInstructionsMobilePhone")}</span>
                  <span className={styles.bankValue}>{PagoMovilNumber}</span>
                </div>
              </div>
            </div>
          </div>

          <form
            onSubmit={handleDeposit}
            noValidate
          >
            <div className={styles.bankFormFields}>{renderFormFields({mobile: false})}</div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 36,
                marginTop: 60,
              }}
            >
              <BaseButton
                variant="primary"
                className="btn-tertiary-hover"
                type="submit"
                loading={isPending}
                disabled={isPending || !canSubmit}
              >
                {t("depositInstructionsContinue")}
              </BaseButton>
              <BaseButton
                variant="secondary"
                className="btn-secondary-hover"
                onClick={onBack}
              >
                {t("depositInstructionsBack")}
              </BaseButton>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

BankVzla.propTypes = {
  onBack: PropTypes.func,
};

export default BankVzla;
